import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Button, LinkButton } from "../../../../components/Button";
import * as Fonts from "../../../../components/Fonts";
import Input from "../../../../components/Form/Input/input";
import { FormGroup } from "../../../../components/Form/styles";
import { KeepoLoader } from "../../../../components/KeepoLoader";
import { SubHeader } from "../../../../components/SubHeader";
import { useToast } from "../../../../components/Toast";
import ToggleSwitch from "../../../../components/ToggleSwitch";
import { useAppState } from "../../../../contexts/App";
import { api, SiteAgeGate } from "../../../../services/api";
import { Card, CardFooter, Container, Section, ToggleRow } from "../../styles";

const prefix = "pages.settings.ageGate";

/** Minimum age the owner can ask for: the API checks it too */
export const MIN_AGE = 13;
export const MAX_AGE = 25;

/**
 * Verificação de idade: the toggle and the minimum age. Visitors confirm their age before seeing the Keepo.
 * Open to every plan.
 */
export const SiteAgeGatePage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { appState, setAppState } = useAppState();
  const siteId = appState.editingSiteId as string;

  // undefined: loading; null: couldn't load
  const [saved, setSaved] = useState<SiteAgeGate | null>();
  const [enabled, setEnabled] = useState(false);
  const [minAge, setMinAge] = useState("18");
  const [isSaving, setSaving] = useState(false);
  const [error, setError] = useState<"range" | "save">();

  useEffect(() => {
    // A late answer for the Keepo we left must not replace this one's
    let isCurrent = true;
    setSaved(undefined);
    api
      .getSiteAgeGate(siteId)
      .then(value => {
        if (!isCurrent) {
          return;
        }
        setSaved(value);
        setEnabled(value.enabled);
        setMinAge(String(value.minAge));
      })
      .catch(() => isCurrent && setSaved(null));
    return () => {
      isCurrent = false;
    };
  }, [siteId]);

  const hasChanges = useMemo(
    () => !!saved && (enabled !== saved.enabled || minAge.trim() !== String(saved.minAge)),
    [enabled, minAge, saved]
  );

  const save = async () => {
    setError(undefined);
    const typed = Number(minAge);
    const isValidAge = Number.isInteger(typed) && typed >= MIN_AGE && typed <= MAX_AGE;
    // Turning it off doesn't need a valid age: a valid one typed is saved, else the saved one stays
    const age = !enabled && saved && !isValidAge ? saved.minAge : typed;
    if (enabled && !isValidAge) {
      setError("range");
      return;
    }
    setSaving(true);
    try {
      const next = await api.updateSiteAgeGate(siteId, enabled, age);
      // The settings list says "Ativo" from this
      setAppState({ ...appState, editingSiteAgeGated: next.enabled });
      // turned on or off, or only another age: a toast says which
      const message =
        next.enabled !== !!saved?.enabled
          ? next.enabled
            ? "turnedOn"
            : "turnedOff"
          : next.minAge !== saved?.minAge && "changed";
      if (message) toast.success(t(`${prefix}.${message}`));
      setSaved(next);
      setEnabled(next.enabled);
      setMinAge(String(next.minAge));
    } catch {
      setError("save");
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    setError(undefined);
    setEnabled(!!saved?.enabled);
    setMinAge(String(saved?.minAge ?? 18));
  };

  const canSave = hasChanges && !isSaving;

  return (
    <Container>
      <SubHeader
        title={t(`${prefix}.title`)}
        crumbs={[{ label: t("pages.settings.header.title"), to: "/settings" }]}
        hasUnsavedChanges={hasChanges}
      />

      <Section>
        <Fonts.Label>{t(`${prefix}.section`)}</Fonts.Label>
        {saved === undefined ? (
          <Card aria-busy="true">
            <Center>
              <KeepoLoader size={24} color="var(--color-neutral-white)" />
            </Center>
          </Card>
        ) : saved === null ? (
          <Card>
            <Message>{t(`${prefix}.loadError`)}</Message>
          </Card>
        ) : (
          <Card
            as="form"
            onSubmit={(event: React.FormEvent) => {
              event.preventDefault();
              if (canSave) {
                save();
              }
            }}>
            <ToggleRow>
              <div className="text">
                <h6>{t(`${prefix}.toggle`)}</h6>
                <p className="description">{t(`${prefix}.toggleHint`)}</p>
              </div>
              <ToggleSwitch
                value={enabled}
                setValue={value => {
                  setError(undefined);
                  setEnabled(value);
                }}
                disabled={isSaving}
                label={t(`${prefix}.toggle`)}
              />
            </ToggleRow>
            <div className="divider" />

            <FormGroup>
              <Input
                id="site-age-gate-min-age"
                type="number"
                label={t(`${prefix}.minAge`)}
                value={minAge}
                ignoreLengthInfo
                disabled={isSaving}
                onChange={event => {
                  setError(undefined);
                  setMinAge(event.target.value);
                }}
                hint={t(`${prefix}.minAgeHint`)}
              />
            </FormGroup>

            <Message>{t(`${prefix}.note`)}</Message>

            <CardFooter>
              {error && (
                <p className="error" role="alert">
                  {t(`${prefix}.errors.${error}`)}
                </p>
              )}
              <div className="actions">
                {hasChanges && <LinkButton onClick={discard}>{t("pages.settings.unsavedBar.discard")}</LinkButton>}
                <Button
                  type="INFO"
                  size="MD"
                  role="button"
                  disabled={!canSave}
                  aria-disabled={!canSave}
                  onClick={() => canSave && save()}>
                  {isSaving ? t("pages.settings.actions.save.button.titleSaving") : t("actions.save")}
                </Button>
              </div>
            </CardFooter>
          </Card>
        )}
      </Section>
    </Container>
  );
};

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 1rem 0;
`;

const Message = styled.p`
  font-family: Inter, sans-serif;
  font-size: 0.75rem;
  line-height: 1rem;
  color: var(--color-neutral-150);
`;
